import { env } from "@/env.js";
export const LOCALSTORAGE_USER_KEY = "flux-forecast-user";

/**
 * Determine whether a user is authenticated or not by looking into the browser cache, this does not actually guarantee the
 * user is authenticated, just that they have a token to use in communication with the API
 *
 * @returns true if the user has a token and it's not expired, false otherwise
 */
export function isAuthenticated() {
    const userInfo = JSON.parse(localStorage.getItem(LOCALSTORAGE_USER_KEY));
    if (userInfo && Object.keys(userInfo).length > 0) {
        if (userInfo?.expires) {
            const expiry = new Date(userInfo.expires * 1000);
            if (expiry <= new Date()) {
                clearAuth();
                return false;
            }
        } else {
            clearAuth();
            return false;
        }
        return true;
    }
    clearAuth();
    return false;
}

export function isDemoUser() {
    return isAuthenticated() && getToken() == "DEMO";
}

/**
 * Initialize the user token for a demonstration of Flux Forecast.
 */
export function initiateDemoToken() {
    // add three hours
    const expiry = Math.floor(Date.now() / 1000) + 10800;
    localStorage.setItem(
        LOCALSTORAGE_USER_KEY,
        JSON.stringify({
            expires: expiry,
            token: "DEMO",
            avatarUrl: "/flux_logo_only.svg",
        }),
    );
}

/**
 * Get the bearer token from browser's local storage
 * @returns A bearer token for use in API communications
 */
export function getToken() {
    const userInfo = JSON.parse(localStorage.getItem(LOCALSTORAGE_USER_KEY));
    return userInfo?.token;
}

/**
 * Send a request to the API to set the password for the user with a specific kid
 * @param {string} kid
 * @param {string} newPassword
 */
export async function setPassword(kid, newPassword) {
    // send a request to the server with the given username and password
    const payload = { kid, newPassword };

    const response = await fetch(`${env.VITE_BASE_API_URL}/login/set-password/`, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
            "Content-Type": "application/json",
        },
    });

    if (!response.ok) {
        const jsonResponse = await response.json();
        const err = new Error("Error trying to set a password");
        err.details = jsonResponse;
        throw err;
    }

    const jsonResponse = await response.json();
    return jsonResponse;
}

/**
 * Send an api request to authenticate the user
 * @param {string} username The user's name
 * @param {string} password The user's password
 * @returns A JSON response with the user's bearer token and expiry on successful authentication
 * @throws Generic Error on any response other than OK (200)
 */
export async function authenticate(username, password) {
    // send a request to the server with the given username and password
    const response = await fetch(`${env.VITE_BASE_API_URL}/login/`, {
        method: "POST",
        body: JSON.stringify({ username, password }),
        headers: {
            "Content-Type": "application/json",
        },
    });

    if (!response.ok) {
        const jsonResponse = await response.json();
        const err = new Error("Error authenticating");
        err.details = jsonResponse;
        throw err;
    }

    const jsonResponse = await response.json();
    localStorage.setItem(LOCALSTORAGE_USER_KEY, JSON.stringify(jsonResponse));
    // store this information in localdb
    return jsonResponse;
}

/**
 * Send a request to the api to send a reset password link to the account.
 *
 * @param {string} email - The account email we wish to send a reset to
 * @throws Error on error otherwise just returns true
 */
export async function requestPasswordReset(body) {
    const response = await fetch(`${env.VITE_BASE_API_URL}/login/forgot`, {
        method: "POST",
        body: JSON.stringify(body),
        headers: {
            "Content-Type": "application/json",
        },
    });

    if (!response.ok) {
        const jsonResponse = await response.json();
        const err = new Error("Error with password reset request");
        err.details = jsonResponse;
        throw err;
    }

    return true;
}

/**
 * Clears the bearer token and user info etc from local storage.
 */
export async function clearAuth() {
    localStorage.removeItem(LOCALSTORAGE_USER_KEY);
}

/**
 * A simple request wrapper which acts like a fetch request, but will put the bearer token in the request for you.
 *
 * @param {string} url
 * @param {string} method one of [GET, POST, PUT, DELETE]
 * @param {Object} body (optional) A JSON-serializable object we want to POST or PUT to the URL
 * @returns
 */
export function authFetch(url, method = "GET", body = {}) {
    if (!["GET", "POST", "DELETE", "PUT"].includes(method)) {
        throw new Error("Unsupported method type");
    }

    if (!isAuthenticated()) {
        throw new Error(
            "Attempted to make authenticated request without authentication",
        );
    }

    const token = getToken();

    const requestOptions = {
        method: method,
        headers: {
            "Content-type": "application/json",
            Authorization: `Bearer ${token}`,
        },
    };
    // if we are using HTTPS then we don't have to worry about replay attacks, apparently.
    if (method === "POST" || method === "PUT") {
        requestOptions.body = JSON.stringify(body);
    }
    return fetch(url, requestOptions);
}
